import type { FC } from "react";
import { memo, useEffect, useRef, useState } from "react";

import Carousel from "@/design-system/carousel";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { LottieAnimation } from "@/src/common/LottieAnimation";
import { useViewportInfoContext } from "@/src/contexts";
import Text from "@Components/Text";
import * as stylex from "@stylexjs/stylex";

import * as css from "./CashbackSteps.module.css";

export interface ICashbackStep {
    id: string;
    title: string;
    description: string;
    animationPath: string;
    svgPath?: string;
    dataTestId?: string;
}

export interface ICashbackSteps {
    cashbackStepsList: ICashbackStep[];
    cashbackStepsAnimationPath: string;
}

const CashbackSteps: FC<ICashbackSteps> = memo(({ cashbackStepsList, cashbackStepsAnimationPath }) => {
    const { isMobile } = useViewportInfoContext();

    const stepsRef = useRef<HTMLDivElement>(null);
    const animTimerRef = useRef<number>();

    const [activeStep, setActiveStep] = useState(-1);

    useEffect(
        () => () => {
            clearInterval(animTimerRef.current);
        },
        [],
    );

    useEffect(() => {
        if (isMobile && activeStep !== -1) {
            sendTelemetryEvent({
                eventName: "homepg-cshbckIntroView",
                actionType: "View",
                param1: `${activeStep + 1}`,
            });
        }
    }, [isMobile, activeStep]);

    const stepDescription = (step: ICashbackStep) => (
        <div key={step.id} className={css.stepDescription}>
            <Text
                dangerouslySetInnerHTML={{ __html: step.title }}
                textElement="p"
                fontStyle="h5"
                fontWeight="bold"
                data-testid={step.dataTestId}
            />
            <Text dangerouslySetInnerHTML={{ __html: step.description }} textElement="p" />
        </div>
    );

    const mobileStep = (step: ICashbackStep) => (
        <div key={step.id} className={css.stepMobile}>
            <img src={step.svgPath} aria-hidden />
            <div className={css.stepDescription}>{stepDescription(step)}</div>
        </div>
    );

    const startAnimation = () => {
        const totalAnimation = 7500;
        const stepNumbers = 4;
        const stepAnimation = totalAnimation / stepNumbers;

        if (stepsRef.current?.children && stepsRef.current.children.length > 0) {
            stepsRef.current.children[0].classList.add(css.startAnim);
        }

        let i = 1;
        animTimerRef.current = window.setInterval(() => {
            if (i > stepNumbers - 1) {
                clearInterval(animTimerRef.current);
                return;
            }

            if (stepsRef.current?.children && stepsRef.current.children.length > 0) {
                stepsRef.current.children[i].classList.add(css.startAnim);
            }
            i += 1;
        }, stepAnimation);
    };

    const clickTelemetryEvent = (param1: string) => {
        sendTelemetryEvent({
            eventName: "homepg-cshbckIntroArrowClick",
            actionType: "Click",
            param1: param1,
        });
    };

    const renderSteps = () => {
        if (isMobile) {
            return (
                <div className={css.stepsMobile}>
                    <Carousel
                        onNextClick={() => {
                            setActiveStep(activeStep + 1);
                            clickTelemetryEvent("4");
                        }}
                        onPrevClick={() => {
                            setActiveStep(activeStep - 1);
                            clickTelemetryEvent("3");
                        }}
                        itemsToShow={1}
                        showArrows={true}
                        styleX={styles.carousel}
                        carouselContainerStyleX={styles.carouselContainer}
                    >
                        {cashbackStepsList?.map(mobileStep)}
                    </Carousel>
                </div>
            );
        }

        return (
            <>
                <div className={css.stepsAnimation}>
                    <LottieAnimation
                        autoplay={true}
                        onDataReady={() => startAnimation()}
                        loop={false}
                        animationPath={cashbackStepsAnimationPath}
                    />
                </div>
                <div className={css.steps} ref={stepsRef}>
                    {cashbackStepsList?.map(stepDescription)}
                </div>
            </>
        );
    };

    return <div className={css.cashbackStepsComponent}>{renderSteps()}</div>;
});

const styles = stylex.create({
    carouselContainer: {
        padding: 0,
        marginBlock: "0",
        marginInline: "-10px",
    },
    carousel: {
        gap: "20px",
        paddingBlock: "0",
        paddingInline: "10px",
        gridAutoColumns: "100%",
        overflowX: "hidden",
    },
});

export default CashbackSteps;
